import React, { useEffect, useRef, useState } from "react";
import Layout from "../Layout/Layout";
import "../css/threads.css";
import ThreadChat from "../Components/ThreadChat";
import { getProfile } from "../actions/login";
import {
  collection,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  startAfter,
  startAt,
  where,
  doc,
  getDoc,
  runTransaction,
  serverTimestamp,
  arrayRemove,
} from "firebase/firestore";
import { db } from "../Utils/firebase";
import { Link, useLocation } from "react-router-dom";
import { getFirestoreUsers } from "../hooks/useFireStore";
import useUserStore from "../zustand-stores/UserStore";
import { FIREBASE_CONVERSATIONS } from "../Utils/constant";

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

export default function Threads() {
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);

  // Threads page / pagination state
  const [threads, setThreads] = useState([]);
  const [threadMessages, setThreadMessages] = useState({}); // { threadId: [msgs] }
  const [hasNext, setHasNext] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const [resolvedParticipants, setResolvedParticipants] = useState({});
  const [verifiedThreadIds, setVerifiedThreadIds] = useState(new Set());

  const pageSize = 2;

  // cursors and listener refs
  const lastDocRef = useRef(null); // Last doc snapshot of current page for scrolling down
  const lastThreadRef = useRef(null); // Ref for the sentinel element for IntersectionObserver

  // to cleanup message listeners for threads on current page
  const messageUnsubscribesRef = useRef({}); // { threadId: unsubscribeFn }

  // get query params for scrolling
  const q = useQuery();
  const scrollTargetRef = useRef(null);
  const hasScrolledToTargetRef = useRef(false);
  const targetConversationId = q.get("conversationId");
  const targetMessageId = q.get("messageId");

  const { user } = useUserStore();

  /* ---------------- Reset unread count for conversation ---------------- */
  const resetUnreadCountInDB = async (conversationId, userId) => {
    const uid = String(userId);
    if (!uid || !conversationId) return;

    const conversationRef = doc(db, FIREBASE_CONVERSATIONS, conversationId);

    try {
      await runTransaction(db, async (transaction) => {
        const convoSnap = await transaction.get(conversationRef);
        if (!convoSnap.exists()) return;

        const convoData = convoSnap.data();
        const currentUnread = convoData.unreadCount || {};

        // Set this user's unread count to 0
        const updatedUnread = { ...currentUnread, [uid]: 0 };

        // Rebuild unreadUserIds array (only users with count > 0 remain)
        const unreadUserIds = Object.keys(updatedUnread).filter(
          (k) => (updatedUnread[k] || 0) > 0,
        );

        transaction.update(conversationRef, {
          unreadCount: updatedUnread,
          unreadUserIds,
          // Clear mention flag for this user so header notification disappears
          lastMessageMentionedIds: arrayRemove(uid),
          updatedAt: serverTimestamp(),
        });
      });
    } catch (error) {
      console.error("Error resetting unread/mention:", error);
    }
  };

  /* ---------------- fetch profile ---------------- */
  useEffect(() => {
    let mounted = true;
    const fetchUserProfile = async () => {
      try {
        const profile = await getProfile();
        if (!mounted) return;
        setUserProfile(profile);
      } catch (error) {
        console.error("Error fetching profile:", error);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchUserProfile();
    return () => {
      mounted = false;
    };
  }, []);

  /* ---------------- Jump to conversation logic ---------------- */
  useEffect(() => {
    if (!userProfile?.id || !targetConversationId) {
      return;
    }

    setPageLoading(true);

    const fetchTargetDocSnapshot = async (documentId) => {
      const docRef = doc(db, "threads", documentId);
      const docSnap = await getDoc(docRef);
      return docSnap.exists() ? docSnap : null;
    };

    const jumpToConversation = async () => {
      try {
        const targetSnapshot =
          await fetchTargetDocSnapshot(targetConversationId);

        if (!targetSnapshot) {
          console.warn("Target conversation not found.");
          setPageLoading(false);
          return;
        }

        const targetThread = {
          id: targetSnapshot.id,
          ...targetSnapshot.data(),
        };

        // Reset unread count for the underlying conversation
        // Thread's conversationId field points to the actual conversation
        if (targetThread.conversationId) {
          await resetUnreadCountInDB(
            targetThread.conversationId,
            userProfile.id,
          );
        }

        const qRef = query(
          collection(db, "threads"),
          where("visibleTo", "array-contains", String(userProfile.id)),
          orderBy("createdAt", "desc"),
          startAfter(targetSnapshot),
          limit(pageSize),
        );

        const snapshot = await getDocs(qRef);
        const otherThreads = snapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));

        setThreads([targetThread, ...otherThreads]);
        lastDocRef.current = snapshot.docs[snapshot.docs.length - 1] || null;
        setHasNext(snapshot.docs.length === pageSize);
        setPageLoading(false);
      } catch (err) {
        console.error("Error jumping to conversation:", err);
        setPageLoading(false);
      }
    };

    jumpToConversation();
  }, [userProfile?.id, targetConversationId]);

  /* ---------------- initial threads fetch (one-time) ---------------- */
  useEffect(() => {
    if (!userProfile?.id || targetConversationId) return;

    setPageLoading(true);

    const qRef = query(
      collection(db, "threads"),
      where("visibleTo", "array-contains", String(userProfile.id)),
      orderBy("createdAt", "desc"),
      limit(pageSize),
    );

    getDocs(qRef)
      .then((snapshot) => {
        const firstPageThreads = snapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));
        setThreads(firstPageThreads);
        lastDocRef.current = snapshot.docs[snapshot.docs.length - 1] || null;
        setHasNext(snapshot.docs.length === pageSize);
        setPageLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching initial threads:", err);
        setPageLoading(false);
      });
  }, [userProfile?.id, targetConversationId]);

  /* ---------------- load more threads on scroll ---------------- */
  const loadMoreThreads = () => {
    if (pageLoading || !hasNext) return;

    const scrollTop = window.scrollY;
    setPageLoading(true);

    const cursor = lastDocRef.current;
    if (!cursor) {
      setPageLoading(false);
      return;
    }

    const qRef = query(
      collection(db, "threads"),
      where("visibleTo", "array-contains", String(userProfile.id)),
      orderBy("createdAt", "desc"),
      startAfter(cursor),
      limit(pageSize),
    );

    getDocs(qRef)
      .then((snapshot) => {
        const newThreads = snapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));
        setThreads((prevThreads) => [...prevThreads, ...newThreads]);
        lastDocRef.current = snapshot.docs[snapshot.docs.length - 1] || null;
        setHasNext(snapshot.docs.length === pageSize);
        setPageLoading(false);
        requestAnimationFrame(() => {
          window.scrollTo(0, scrollTop);
        });
      })
      .catch((err) => {
        console.error("Error loading more threads:", err);
        setPageLoading(false);
      });
  };

  /* ---------------- IntersectionObserver for infinite scrolling ---------------- */
  useEffect(() => {
    if (pageLoading || !lastThreadRef.current || !hasNext) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMoreThreads();
        }
      },
      {
        root: null,
        rootMargin: "0px",
        threshold: 0.5,
      },
    );

    observer.observe(lastThreadRef.current);

    return () => {
      if (lastThreadRef.current) {
        observer.unobserve(lastThreadRef.current);
      }
    };
  }, [pageLoading, hasNext, threads]);

  /* ---------------- per-page thread messages listeners ---------------- */
  useEffect(() => {
    const existing = messageUnsubscribesRef.current || {};
    Object.keys(existing).forEach((tid) => {
      if (!threads.find((t) => t.id === tid)) {
        try {
          existing[tid]();
        } catch (e) {
          console.warn("Error unsubscribing message listener:", e);
        }
        delete existing[tid];
      }
    });

    threads.forEach((thread) => {
      if (messageUnsubscribesRef.current[thread.id]) return;

      const messagesRef = collection(db, "threads", thread.id, "messages");
      const qRef = query(messagesRef, orderBy("timestamp", "asc"));

      const unsub = onSnapshot(
        qRef,
        (snapshot) => {
          const msgs = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
          setThreadMessages((prev) => ({ ...prev, [thread.id]: msgs }));
        },
        (err) => {
          console.error(
            `Realtime messages error for thread ${thread.id}:`,
            err,
          );
        },
      );

      messageUnsubscribesRef.current[thread.id] = unsub;
    });
  }, [threads]);

  // cleanup message listeners on unmount
  useEffect(() => {
    return () => {
      const existing = messageUnsubscribesRef.current || {};
      Object.keys(existing).forEach((tid) => {
        try {
          existing[tid]();
        } catch (e) {
          /* ignore */
        }
      });
      messageUnsubscribesRef.current = {};
    };
  }, []);

  /* ---------------- scroll to target thread if provided in URL ---------------- */
  useEffect(() => {
    hasScrolledToTargetRef.current = false;
  }, [targetConversationId]);

  useEffect(() => {
    if (
      !targetConversationId ||
      hasScrolledToTargetRef.current ||
      !scrollTargetRef.current ||
      !verifiedThreadIds.has(targetConversationId)
    ) {
      return;
    }

    const timeoutId = setTimeout(() => {
      scrollTargetRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
      hasScrolledToTargetRef.current = true;
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [threads, targetConversationId, verifiedThreadIds]);

  const getThreadParticipants = async (thread) => {
    const visibleToUserIds = thread?.visibleTo ?? [];
    const fetchIds = visibleToUserIds
      .filter((uid) => String(uid) !== String(user?.id))
      .map((uid) => String(uid));

    if (fetchIds.length === 0) return {};

    const threadParticipants = await getFirestoreUsers(fetchIds);

    const participantsMap = threadParticipants.reduce((acc, u) => {
      const key = String(u?.uid ?? u?.id ?? "");
      if (!key) return acc;
      acc[key] = {
        name: u?.name || u?.full_name || u?.displayName || "",
        profileImage: u?.profileImage || u?.image || "",
      };
      return acc;
    }, {});

    return participantsMap;
  };

  useEffect(() => {
    if (threads.length === 0 || !userProfile?.id) return;

    const resolveParticipants = async () => {
      const newResolved = { ...resolvedParticipants };
      let updated = false;

      for (const thread of threads) {
        // Only fetch participants if they haven't been resolved yet
        if (newResolved[thread.id]) continue;

        // ASYNC CALL
        const participantsMap = await getThreadParticipants(thread);

        // STORE result using thread ID as the key
        newResolved[thread.id] = participantsMap;
        updated = true;
      }

      if (updated) {
        setResolvedParticipants(newResolved);
      }
    };

    resolveParticipants();
  }, [threads, userProfile?.id]);

  /* ---------------- Sanity Check: Verify parent conversation status ---------------- */
  useEffect(() => {
    if (threads.length === 0 || !userProfile?.id) return;

    const verifyThreads = async () => {
      const newVerified = new Set(verifiedThreadIds);
      let changed = false;

      for (const thread of threads) {
        if (newVerified.has(thread.id)) continue;

        // Direct Check: If thread has a conversationId, verify it
        if (thread.conversationId) {
          try {
            const convoRef = doc(
              db,
              FIREBASE_CONVERSATIONS,
              thread.conversationId,
            );
            const convoSnap = await getDoc(convoRef);

            if (convoSnap.exists()) {
              const convoData = convoSnap.data();
              const participants = (convoData.participants || []).map(String);
              const visibleTo = (convoData.visibleTo || []).map(String);
              const uidStr = String(userProfile.id);

              const isInParticipants = participants.includes(uidStr);
              const isVisibleInMain = visibleTo.includes(uidStr);

              // Safety: If group, also check participantDetails keys
              const isInDetails =
                convoData.type === "group"
                  ? convoData.participantDetails &&
                    uidStr in convoData.participantDetails
                  : true;

              if (isInParticipants && isVisibleInMain && isInDetails) {
                newVerified.add(thread.id);
                changed = true;
              }
            }
          } catch (err) {
            console.error(`Error verifying thread ${thread.id}:`, err);
          }
        }
      }

      if (changed) {
        setVerifiedThreadIds(newVerified);
      }
    };

    verifyThreads();
  }, [threads, userProfile?.id]);

  /* ---------------- UI ---------------- */
  if (loading) {
    return (
      <Layout showHeader={false}>
        <div className="page-content">
          <div className="setting-content threads-main">
            <div className="user-threads-card d-flex flex-column">
              <span className="d-flex justify-content-center">
                Loading conversations...
              </span>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout showHeader={false}>
      <div className="page-content">
        <div className="setting-content threads-main">
          <div className="d-flex align-items-center gap-2 pages-title-container">
            <img className="sidebar-toggle" src="/icons/menu.svg" alt="menu" />
            <h1 className="pages-header text-heading-h2 mb-0">Chats</h1>
          </div>

          <div className="user-threads-card d-flex flex-column">
            <div className="d-flex justify-content-between threads-chat-content align-items-center">
              <div className="d-flex gap-2 gap-sm-3 text-body-b4">
                <Link
                  className="btn-1 btn-1-basic chat-btn-1-basic text-decoration-none"
                  style={{ width: "81px", height: "30px" }}
                  to={"/chat"}
                >
                  All{" "}
                </Link>
                <button
                  className="chat-all-btn"
                  style={{ maxWidth: "81px", width: "100%", height: "30px" }}
                >
                  Threads
                </button>
                <button
                  className="btn-1 btn-1-basic chat-btn-1-basic"
                  style={{ maxWidth: "78px", width: "100%", height: "30px" }}
                  onClick={() => (window.location.href = "/squads")}
                >
                  Squads
                </button>
              </div>
            </div>

            <div className="flex-grow-1">
              {threads.filter((t) => verifiedThreadIds.has(t.id)).length ===
                0 && !pageLoading ? (
                <div className="d-flex justify-content-center mt-3">
                  No conversations found.
                </div>
              ) : (
                threads
                  .filter((t) => verifiedThreadIds.has(t.id))
                  .map((thread) => {
                    const isTargetThread = thread.id === targetConversationId;
                    return (
                      <div
                        key={thread.id}
                        ref={isTargetThread ? scrollTargetRef : null}
                        style={
                          isTargetThread
                            ? {
                                animation: "highlightFade 2s ease-out",
                                borderRadius: "8px",
                              }
                            : {}
                        }
                      >
                        <style>
                          {`
                          @keyframes highlightFade {
                            0% { background-color: rgba(0, 140, 255, 0.3); }
                            100% { background-color: transparent; }
                          }
                        `}
                        </style>

                        <ThreadChat
                          currentUserId={userProfile?.id}
                          threadId={thread.id}
                          conversationId={thread.conversationId}
                          threadParticipants={
                            resolvedParticipants[thread.id] || {}
                          }
                        />
                      </div>
                    );
                  })
              )}
              {/* This is the new sentinel element for the observer */}
              <div ref={lastThreadRef}></div>
              {pageLoading && (
                <div className="d-flex justify-content-center mt-3">
                  Loading more conversations...
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
